import ToolReceipt from './ToolReceipt.js?v=1.0.18';
import ToolWorkspace from './ToolWorkspace.js?v=1.0.9';

const SOURCE_ACTIVITY_TYPES = new Set([
  'source_read',
  'source-reader',
  'source_reader',
]);
const SOURCE_STATUSES = new Set([
  'running',
  'completed',
  'partial',
  'failed',
  'cancelled',
]);
const LOCATOR_FIELDS = new Set([
  'page', 'pages', 'pagina', 'article', 'articulo', 'paragraph', 'parrafo',
  'section', 'seccion', 'clause', 'clausula', 'block', 'bloque', 'chunk',
  'court', 'tribunal', 'chamber', 'sala', 'date', 'fecha', 'decision_number',
  'effective_date', 'jurisdiction',
]);

function stringValue(value, limit) {
  return String(value == null ? '' : value)
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
    .slice(0, limit);
}

function normalizeLocator(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.keys(value).reduce((locator, key) => {
    if (!LOCATOR_FIELDS.has(key)) return locator;
    const rawValue = value[key];
    if (Array.isArray(rawValue)) {
      locator[key] = rawValue
        .filter(item => ['string', 'number'].includes(typeof item))
        .slice(0, 20)
        .map(item => stringValue(item, 160));
    } else if (['string', 'number'].includes(typeof rawValue)) {
      locator[key] = stringValue(rawValue, 160);
    }
    return locator;
  }, {});
}

function normalizeInlinePassage(value, index) {
  if (!value || typeof value !== 'object' || typeof value.quote !== 'string') return null;
  const anchorId = stringValue(value.anchor_id || `summary-passage-${index + 1}`, 200);
  return {
    anchor_id: anchorId,
    source_title: stringValue(value.source_title || value.title || '', 300),
    quote: stringValue(value.quote, 20000),
    revision: stringValue(value.revision || '', 200),
    locator: normalizeLocator(value.locator),
    match_kind: stringValue(value.match_kind || '', 80),
    content_truncated: value.content_truncated === true,
  };
}

function sourceActivityValue(activity) {
  if (!activity || typeof activity !== 'object') return {};
  return activity.receipt && typeof activity.receipt === 'object'
    ? { ...activity, ...activity.receipt }
    : activity;
}

export function isSummarySourceReadActivity(activity) {
  const value = sourceActivityValue(activity);
  const toolName = stringValue(value.tool_name, 80).trim().toLocaleLowerCase('es-ES');
  if (toolName === 'leer_fuente') return true;
  const activityType = stringValue(
    value.activity_type || value.type || value.kind,
    80,
  ).trim().toLocaleLowerCase('es-ES');
  return SOURCE_ACTIVITY_TYPES.has(activityType);
}

export function summarySourceToolRunId(activity) {
  const value = sourceActivityValue(activity);
  return stringValue(value.tool_run_id || value.toolRunId || '', 200).trim();
}

export function inlinePassagesForSummaryActivity(activity) {
  if (!activity || typeof activity !== 'object') return null;
  const value = sourceActivityValue(activity);
  const candidates = [
    activity.inline_passages,
    activity.inlinePassages,
    value.inline_passages,
    value.inlinePassages,
    activity.passages,
    value.passages,
  ];
  const passages = candidates.find(candidate => Array.isArray(candidate));
  if (!passages) return null;
  return passages
    .slice(0, 50)
    .map(normalizeInlinePassage)
    .filter(Boolean);
}

export function receiptForSummarySourceActivity(activity) {
  const value = sourceActivityValue(activity);
  const status = stringValue(value.status || 'running', 40).trim().toLocaleLowerCase('es-ES');
  const uiPayload = value.ui_payload && typeof value.ui_payload === 'object'
    ? value.ui_payload
    : {};
  const actions = Array.isArray(uiPayload.actions)
    ? uiPayload.actions.filter(item => typeof item === 'string').slice(0, 10)
    : [];
  const inlinePassages = inlinePassagesForSummaryActivity(activity);
  const hasOpenPassagesAction = actions.some(action => (
    String(action || '')
      .trim()
      .toLocaleLowerCase('es-ES')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') === 'abrir pasajes'
  ));
  if (Array.isArray(inlinePassages) && inlinePassages.length > 0 && !hasOpenPassagesAction) {
    actions.push('Abrir pasajes');
  }
  return {
    tool_run_id: summarySourceToolRunId(activity),
    tool_name: 'leer_fuente',
    status: SOURCE_STATUSES.has(status) ? status : 'running',
    state_version: Number(value.state_version) || 0,
    event_seq: Number(value.event_seq || value.activity_seq) || 0,
    created_at: value.created_at || value.timestamp || null,
    ui_payload: {
      title: stringValue(uiPayload.title || 'Pasajes verificados', 300),
      summary: stringValue(uiPayload.summary || value.message || '', 1000),
      counts: uiPayload.counts && typeof uiPayload.counts === 'object'
        ? { ...uiPayload.counts }
        : {},
      warnings: Array.isArray(uiPayload.warnings)
        ? uiPayload.warnings.filter(item => typeof item === 'string').slice(0, 20)
        : [],
      actions,
    },
  };
}

export function upsertSummarySourceActivity(history, activity, now = Date.now()) {
  if (!Array.isArray(history) || !isSummarySourceReadActivity(activity)) {
    return { changed: false, inserted: false, index: -1, activity: null };
  }
  const toolRunId = summarySourceToolRunId(activity);
  if (!toolRunId) {
    return { changed: false, inserted: false, index: -1, activity: null };
  }
  const index = history.findIndex(item => (
    isSummarySourceReadActivity(item)
    && summarySourceToolRunId(item) === toolRunId
  ));
  const incomingTimestamp = activity.created_at || activity.timestamp;
  if (index >= 0) {
    const existing = history[index];
    const existingStatus = stringValue(existing.status || '', 40).trim().toLocaleLowerCase('es-ES');
    const incomingStatus = stringValue(activity.status || '', 40).trim().toLocaleLowerCase('es-ES');
    const existingVersion = Number(
      existing.state_version || existing.event_seq || existing.activity_seq || 0,
    );
    const incomingVersion = Number(
      activity.state_version || activity.event_seq || activity.activity_seq || 0,
    );
    const existingIsTerminal = existingStatus && existingStatus !== 'running';
    if (
      (existingIsTerminal && incomingStatus === 'running')
      || (
        existingVersion > 0
        && incomingVersion > 0
        && incomingVersion < existingVersion
      )
    ) {
      return { changed: false, inserted: false, index, activity: existing };
    }
    const stableTimestamp = existing.timestamp || incomingTimestamp || now;
    const stableId = existing.id || `source:${toolRunId}`;
    Object.assign(existing, activity, {
      tool_name: 'leer_fuente',
      tool_run_id: toolRunId,
      id: stableId,
      timestamp: stableTimestamp,
    });
    return { changed: true, inserted: false, index, activity: existing };
  }
  const inserted = {
    ...activity,
    tool_name: 'leer_fuente',
    tool_run_id: toolRunId,
    id: `source:${toolRunId}`,
    timestamp: incomingTimestamp || now,
  };
  history.push(inserted);
  return {
    changed: true,
    inserted: true,
    index: history.length - 1,
    activity: inserted,
  };
}

function activitySequence(activity) {
  const sequence = Number(activity && activity.activity_seq);
  return Number.isInteger(sequence) && sequence > 0 ? sequence : null;
}

function activityTimestamp(activity) {
  const rawValue = activity && (activity.timestamp || activity.created_at);
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) return rawValue;
  if (typeof rawValue !== 'string' || !rawValue.trim()) return null;
  const timestamp = new Date(rawValue).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function chronologicalInsertionIndex(activities, activity) {
  const sequence = activitySequence(activity);
  if (sequence !== null) {
    const sequenceIndex = activities.findIndex(item => {
      const itemSequence = activitySequence(item);
      return itemSequence !== null && itemSequence > sequence;
    });
    if (sequenceIndex >= 0) return sequenceIndex;
    if (activities.some(item => activitySequence(item) !== null)) {
      for (let index = activities.length - 1; index >= 0; index -= 1) {
        if (activitySequence(activities[index]) !== null) return index + 1;
      }
    }
  }

  const timestamp = activityTimestamp(activity);
  if (timestamp !== null) {
    const timestampIndex = activities.findIndex(item => {
      const itemTimestamp = activityTimestamp(item);
      return itemTimestamp !== null && itemTimestamp > timestamp;
    });
    if (timestampIndex >= 0) return timestampIndex;
  }
  return activities.length;
}

export function reconcileCachedSummaryActivities(cachedActivities, liveActivities) {
  const reconciled = Array.isArray(cachedActivities)
    ? cachedActivities.map(activity => ({ ...activity }))
    : [];
  const liveSourceActivities = Array.isArray(liveActivities)
    ? liveActivities.filter(isSummarySourceReadActivity)
    : [];

  liveSourceActivities.forEach(liveActivity => {
    const toolRunId = summarySourceToolRunId(liveActivity);
    if (!toolRunId) return;
    const cachedIndex = reconciled.findIndex(activity => (
      isSummarySourceReadActivity(activity)
      && summarySourceToolRunId(activity) === toolRunId
    ));
    if (cachedIndex >= 0) {
      const mergedSource = [{ ...liveActivity }];
      upsertSummarySourceActivity(mergedSource, reconciled[cachedIndex]);
      reconciled[cachedIndex] = mergedSource[0];
      return;
    }

    const retainedSource = { ...liveActivity };
    reconciled.splice(
      chronologicalInsertionIndex(reconciled, retainedSource),
      0,
      retainedSource,
    );
  });
  return reconciled;
}

const SummarySourceReadReceipt = {
  props: {
    receipt: { type: Object, required: true },
    inlinePassages: { type: Array, default: null },
  },
  components: {
    ToolReceipt,
    ToolWorkspace,
  },
  data() {
    return {
      currentReceipt: this.receipt,
      currentInlinePassages: this.inlinePassages,
      activeReceipt: null,
      returnFocus: null,
    };
  },
  methods: {
    normalizeAction(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase('es-ES')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    },
    handleAction({ action } = {}) {
      if (
        this.normalizeAction(action) !== 'abrir pasajes'
        || !Array.isArray(this.currentInlinePassages)
        || this.currentInlinePassages.length === 0
      ) return;
      this.returnFocus = typeof document !== 'undefined' ? document.activeElement : null;
      this.activeReceipt = this.currentReceipt;
    },
    updateActivity(receipt, inlinePassages) {
      const workspaceOpen = Boolean(this.activeReceipt);
      this.currentReceipt = receipt;
      this.currentInlinePassages = inlinePassages;
      if (workspaceOpen) this.activeReceipt = receipt;
    },
    isWorkspaceOpen() {
      return Boolean(this.activeReceipt);
    },
    closeWorkspace() {
      this.activeReceipt = null;
      this.$nextTick(() => {
        const target = this.returnFocus;
        this.returnFocus = null;
        if (target && document.contains(target) && typeof target.focus === 'function') {
          target.focus({ preventScroll: true });
        }
      });
    },
  },
  template: `
    <div class="summary-source-read-receipt">
      <tool-receipt
        :receipt="currentReceipt"
        :inline-passages="currentInlinePassages"
        @action="handleAction"
      />
      <teleport to="body">
        <tool-workspace
          class="summary-source-read-workspace"
          :receipt="activeReceipt"
          :inline-passages="currentInlinePassages"
          @close="closeWorkspace"
        />
      </teleport>
    </div>
  `,
};

export function installSummarySourceReaderBridge(createApp) {
  if (typeof window === 'undefined' || typeof createApp !== 'function') return null;
  const mountedApps = new WeakMap();
  const bridge = {
    isActivity: isSummarySourceReadActivity,
    toolRunId: summarySourceToolRunId,
    inlinePassages: inlinePassagesForSummaryActivity,
    receipt: receiptForSummarySourceActivity,
    upsertActivity: upsertSummarySourceActivity,
    reconcileActivities: reconcileCachedSummaryActivities,
    openToolRunId(container) {
      const mounted = container && mountedApps.get(container);
      if (
        !mounted
        || !mounted.instance
        || typeof mounted.instance.isWorkspaceOpen !== 'function'
        || !mounted.instance.isWorkspaceOpen()
      ) return '';
      return mounted.toolRunId;
    },
    unmount(container) {
      if (!container || !mountedApps.has(container)) return;
      try { mountedApps.get(container).app.unmount(); } catch (error) { }
      mountedApps.delete(container);
    },
    mount(container, activity) {
      if (!container || !isSummarySourceReadActivity(activity)) return null;
      const toolRunId = summarySourceToolRunId(activity);
      const receipt = receiptForSummarySourceActivity(activity);
      const inlinePassages = inlinePassagesForSummaryActivity(activity);
      const existing = mountedApps.get(container);
      if (
        existing
        && existing.toolRunId === toolRunId
        && existing.instance
        && typeof existing.instance.updateActivity === 'function'
      ) {
        existing.instance.updateActivity(receipt, inlinePassages);
        return existing.app;
      }
      this.unmount(container);
      const app = createApp(SummarySourceReadReceipt, {
        receipt,
        inlinePassages,
      });
      const instance = app.mount(container);
      mountedApps.set(container, { app, instance, toolRunId });
      return app;
    },
  };
  window.lexiusSummarySourceReader = bridge;
  try {
    window.dispatchEvent(new CustomEvent('lexius-summary-source-reader-ready'));
  } catch (error) { }
  return bridge;
}
